import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import AppCell from '@/components/common/app-cell';
import Toggle from '@/components/common/toggle';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useClearAllDeviceSession from '@/hooks/conversations/use-clear-all-device-session';
import useUserPlatform from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import { useConfigurationStore } from '@/stores/configuration';
import { useAlwaysEnterAsSubmit, useDeviceStore, useEmbedLayout } from '@/stores/device';
import { makePrompt } from '@/stores/prompter';
import { EmbedLayout } from '@/utils/embed-layout';

export default function MessagingSettingsRoute() {
  const { t } = useTranslation();
  const [searchParams] = useTypedSearchParams(ROUTES.MY.SETTINGS.MESSAGING);
  const { clearAllDeviceSession } = useClearAllDeviceSession();
  const { enterAsSubmit, setEnterAsSubmit } = useDeviceStore();
  const layout = useEmbedLayout();
  const { dispatch } = useCommDispatcher();
  const { isEmbedded, isDesktop } = useUserPlatform();
  const alwaysEnterAsSubmit = useAlwaysEnterAsSubmit();
  const navigate = useNavigate();

  useEffect(() => {
    if (isEmbedded) {
      useConfigurationStore.persist.rehydrate();
    }
  }, []);

  const handleClearAllMessages = () =>
    makePrompt({
      key: 'clear-all-messages',
      type: 'dialog',
      title: t('messaging-settings.clear-chat'),
      content: t('messaging-settings.clear-chat-confirm'),
      promise: () => clearAllDeviceSession(),
    });

  return (
    <PageLayout
      title={t('mine.messaging')}
      className='mode-embedded:gap-0 mode-embedded:bg-neutral-100'
      headerConfig={{
        showHeader: layout >= EmbedLayout.COMPACT,
        onNavigateBack: () => (searchParams.from === 'onboard' ? dispatch({ event: 'dismiss' }) : undefined),
        navigateBack: !isDesktop,
      }}>
      <div className='flex flex-col mode-embedded:bg-neutral-0 mode-embedded:rounded-md mode-embedded:m-5'>
        {!isDesktop && !alwaysEnterAsSubmit && (
          <AppCell
            label={
              isEmbedded ? t('messaging-settings.submit-by-pressing-enter') : t('messaging-settings.enter-as-submit')
            }
            suffix={
              <Toggle
                checked={enterAsSubmit ?? false}
                onChange={(value) => setEnterAsSubmit(value)}
              />
            }
          />
        )}

        <Link to={ROUTES.MY.STICKERS.$path()}>
          <AppCell
            hoverable
            label={t('stickers.my-stickers')}
            chevron
          />
        </Link>

        <AppCell
          label={t('messaging-settings.batch-message.assistant')}
          chevron
          onClick={() => navigate(ROUTES.MY.SETTINGS.MESSAGING.BATCH.$path())}
        />
        <AppCell
          labelClassName='mode-embedded:text-error'
          label={t('messaging-settings.clear-chat')}
          onClick={handleClearAllMessages}
        />
      </div>
    </PageLayout>
  );
}
